import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { storage } from '../../helpers/firebase';
import './fileUploader.css';

const FileUploader = ({
  accept = [],
  name = 'file',
  filename: filenameGenerator,
  storageRef,
  onUploadSuccess,
  onUploadError,
  onUploadStart,
  onProgress,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);
  const inputIdRef = useRef(`file-input-${name}-${Math.random().toString(36).slice(2, 9)}`);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      // Reset states
      setUploading(true);
      setUploadError(null);
      setUploadSuccess(false);
      setProgress(0);

      // Call onUploadStart if provided
      if (onUploadStart) {
        onUploadStart(file);
      }

      // Generate filename using the provided generator function
      let uploadFilename = file.name;
      if (filenameGenerator && typeof filenameGenerator === 'function') {
        uploadFilename = filenameGenerator(file);
      }

      // Get the proper path reference
      // storageRef can be a path string, Storage instance, or StorageReference.
      let pathRef;
      if (typeof storageRef === 'string') {
        const basePath = storageRef.replace(/\/+$/, '');
        pathRef = ref(storage, `${basePath}/${uploadFilename}`);
      } else {
        // Assume it is already a Storage instance or StorageReference.
        pathRef = ref(storageRef, uploadFilename);
      }

      // Simulate progress updates (Firebase doesn't provide native progress in v10)
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev < 90) {
            return prev + Math.random() * 20;
          }
          return prev;
        });
      }, 100);

      // Upload file
      const uploadResult = await uploadBytes(pathRef, file);
      clearInterval(progressInterval);
      setProgress(100);

      // Get download URL
      const downloadUrl = await getDownloadURL(uploadResult.ref);

      // Call onUploadSuccess if provided
      if (onUploadSuccess) {
        onUploadSuccess(uploadFilename, downloadUrl, uploadResult);
      }

      setUploadSuccess(true);
      setUploading(false);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Clear success message after 3 seconds
      setTimeout(() => setUploadSuccess(false), 3000);
    } catch (error) {
      setUploading(false);
      setUploadError(error.message);

      if (onUploadError) {
        onUploadError(error);
      }

      // Clear error message after 5 seconds
      setTimeout(() => setUploadError(null), 5000);
    }
  };

  // Convert MIME types to file extension filter if needed
  const acceptString = accept && accept.length > 0 ? accept.join(',') : 'image/*';

  return (
    <div className="file-uploader">
      <div className="file-input-wrapper">
        <input
          id={inputIdRef.current}
          ref={fileInputRef}
          type="file"
          accept={acceptString}
          name={name}
          onChange={handleFileChange}
          disabled={uploading}
          className="file-input"
        />
        <label
          htmlFor={inputIdRef.current}
          aria-disabled={uploading}
          className={`file-input-label ${uploading ? 'disabled' : ''}`}
        >
          {uploading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
              Uploading... {Math.round(progress)}%
            </>
          ) : (
            'Choose File'
          )}
        </label>
      </div>

      {uploadError && (
        <div className="alert alert-danger alert-sm mt-2" role="alert">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
          {uploadError}
        </div>
      )}

      {uploadSuccess && (
        <div className="alert alert-success alert-sm mt-2" role="alert">
          <FontAwesomeIcon icon={faCheck} className="mr-2" />
          Upload successful
        </div>
      )}
    </div>
  );
};

FileUploader.propTypes = {
  accept: PropTypes.arrayOf(PropTypes.string),
  name: PropTypes.string,
  filename: PropTypes.func,
  storageRef: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  onUploadSuccess: PropTypes.func,
  onUploadError: PropTypes.func,
  onUploadStart: PropTypes.func,
  onProgress: PropTypes.func,
};

export default FileUploader;
